/**
 * FIN-13389 — immutable updates for a variant's `function` array.
 *
 * The Formula tab's selectable-condition list is `QueryBuilder.allFunc`, a `useMemo` keyed on
 * `variantInfo?.function`. Functions.js used to mutate that array (and its elements) in place and
 * return only a shallow copy of the wrapper object, so the array kept its identity, the memo never
 * recomputed, and a newly added function never became selectable as a condition. The Functions tab
 * itself looked correct because it reads the array directly during render rather than through a
 * memo — which is exactly why the bug read as "saves fine, shows in the list, unusable in Formula".
 *
 * Every helper here returns new identities for the levels it touches and leaves everything else
 * referentially untouched, so downstream memos recompute exactly when they should.
 *
 * Pure and antd-free on purpose: importing QueryBuilder.js pulls in config.js and antd and fails
 * under jest, which is why PR #11533 extracted functionReturnType.js the same way.
 */

/** Append a function, giving the array a new identity. */
export const addFunction = (functionInfo, newFunction) => ({
  ...functionInfo,
  function: [...(functionInfo?.function || []), newFunction]
});

/**
 * Update one function's `args`.
 *
 * @param merge  true (default) merges into the existing args; false replaces them wholesale, which
 *               is what the Applicable To change does — it deliberately resets lookback etc.
 */
export const patchFunctionArgs = (
  functionInfo,
  index,
  argsPatch,
  replace = false
) => {
  const current = functionInfo?.function?.[index];
  if (!current) {
    return functionInfo;
  }
  const nextFunctions = [...functionInfo.function];
  nextFunctions[index] = {
    ...current,
    args: replace ? { ...argsPatch } : { ...current.args, ...argsPatch }
  };
  return { ...functionInfo, function: nextFunctions };
};

/**
 * Write a variant back into the typology tree without mutating the previous state.
 *
 * The old write-back did `let dataToSet = prev; dataToSet[t].variants[v] = variant;` — mutating
 * React state in place before returning a new outer array, so the nested levels kept their
 * identities and any consumer memoising on them was left stale.
 */
export const replaceVariant = (
  variantRenderData,
  typoIndex,
  variantIndex,
  variant
) => {
  const typology = variantRenderData?.[typoIndex];
  if (!typology?.variants?.[variantIndex]) {
    return variantRenderData;
  }
  const nextVariants = [...typology.variants];
  nextVariants[variantIndex] = variant;

  const next = [...variantRenderData];
  next[typoIndex] = { ...typology, variants: nextVariants };
  return next;
};

/**
 * Whether the drawer's record can be re-synced from `variantRenderData`.
 *
 * Needs BOTH indices: three of the four `showDrawer` callers pass only `(defaultTab, record)`, so
 * there is no slot to read from and the record stays as opened.
 *
 * Replaces `state.record.variantIndex > -1`, which was right by accident. It missed `typoIndex`
 * entirely, and `null > -1` coerces to `0 > -1` and PASSES — so a record carrying null indices
 * reached `variantRenderData[null].variants[null]` and threw inside
 * getDerivedStateFromProps, unmounting the drawer.
 */
export const shouldResyncRecord = (record) =>
  Number.isInteger(record?.typoIndex) &&
  Number.isInteger(record?.variantIndex) &&
  record.typoIndex >= 0 &&
  record.variantIndex >= 0;
